import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Trash2, Upload, Image as ImageIcon, Loader2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useSeo } from "../lib/useSeo";

export default function AdminShowcase() {
  useSeo({ title: "Admin — Showcase Gallery", noindex: true });
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ category: "Cinematic & Camera Shots", command_slug: "", caption: "", attribution: "", order: 0 });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/showcase${filter ? `?category=${encodeURIComponent(filter)}` : ""}`);
      setItems(data);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose an image first"); return; }
    if (!form.category) { toast.error("Pick a category"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", form.category);
    fd.append("command_slug", form.command_slug || "");
    fd.append("caption", form.caption || "");
    fd.append("attribution", form.attribution || "");
    fd.append("order", String(form.order || 0));
    setUploading(true);
    try {
      await api.post("/admin/showcase/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image added");
      setFile(null); if (fileRef.current) fileRef.current.value = "";
      setForm({ ...form, caption: "", command_slug: "", order: 0 });
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setUploading(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this showcase image?")) return;
    try {
      await api.delete(`/admin/showcase/${id}`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={upload} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="admin-showcase-upload-form">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><ImageIcon size={18} /> Add Showcase Image</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Category</span>
            <select data-testid="showcase-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none">
              {cats.map((c) => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Command slug (optional)</span>
            <input data-testid="showcase-command-slug" value={form.command_slug} onChange={(e) => setForm({ ...form, command_slug: e.target.value })}
              placeholder="e.g. fisheye" className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Caption</span>
            <input data-testid="showcase-caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })}
              placeholder="A short line visible under the image" className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none" />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Attribution (optional)</span>
            <input data-testid="showcase-attribution" value={form.attribution} onChange={(e) => setForm({ ...form, attribution: e.target.value })}
              placeholder='e.g. "Generated in ChatGPT by Diane"' className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none" />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Display order</span>
            <input data-testid="showcase-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Image file (PNG / JPG / WEBP, max 8MB)</span>
            <input data-testid="showcase-file" ref={fileRef} type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 w-full text-sm" />
          </label>
        </div>
        <button data-testid="showcase-upload-btn" type="submit" disabled={uploading}
          className="inline-flex items-center gap-2 bg-[#0B0F0D] text-[#D4AF37] font-bold px-5 py-2.5 rounded-xl hover:bg-[#1a2420] disabled:opacity-50">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {uploading ? "Uploading…" : "Upload image"}
        </button>
      </form>

      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold">Gallery ({items.length})</h2>
        <select data-testid="showcase-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm">
          <option value="">All categories</option>
          {cats.map((c) => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-500">
          No showcase images yet. Add your first one above.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="showcase-grid">
          {items.map((s) => (
            <div key={s.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`showcase-item-${s.id}`}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${s.url}`} alt={s.caption || s.command_slug || "showcase"}
                className="w-full aspect-[4/5] object-cover bg-slate-100" loading="lazy" />
              <div className="p-3">
                <p className="text-xs font-semibold text-slate-800 line-clamp-2">{s.caption || "—"}</p>
                <p className="text-[11px] text-slate-500 mt-1">
                  {s.command_slug ? `/${s.command_slug}` : "—"} · {s.category}
                </p>
                <button data-testid={`showcase-delete-${s.id}`} onClick={() => remove(s.id)}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700">
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
